import { useState } from 'react';

import { Property } from '@ts-types/property';

import { usePropsDataStore } from '@store/PropsDataStore';

import { generateForestandProperty } from '@services/property';

import { Button, HelperText, Icon, Text } from '@ui-component-library';

import { DynamicModal } from '@components/DynamicModal';

import { cn } from '../../../../../utils/cn';
import type { SearchPropertyResult } from '../../PropertySelector.types';
import { validateForestandFile } from '../../PropertySelector.utils';

import { AlertDialog } from '../AlertModal';
import { FileUpload } from '../FileUpload';
import { SearchAndAddProperties } from '../SearchAndAddProperties';

import type { UploadForestandFileProps } from './UploadForestandFile.types';

const UploadForestandFile = ({ className, isOpen, onClose }: UploadForestandFileProps) => {
  const { setOneProp } = usePropsDataStore();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [linkedProperty, setLinkedProperty] = useState<SearchPropertyResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined);

  const cleanupStates = () => {
    setUploadedFile(null);
    setLinkedProperty(null);
  };

  const handleClose = () => {
    onClose();
    cleanupStates();
  };

  const handleCloseErrorModal = () => {
    setErrorMessage(undefined);
  };

  const generateForestandSBP = async (linkedPropertyOverride?: SearchPropertyResult | null) => {
    if (!uploadedFile) {
      return;
    }

    const propertyToUse = linkedPropertyOverride ?? linkedProperty;

    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append('file', uploadedFile);

      if (propertyToUse) {
        formData.append('lmID', propertyToUse.registerenhet);
        formData.append('designation_overwrite', propertyToUse.beteckning);
      }

      const resp = await generateForestandProperty(formData);

      if (resp.ok) {
        let volume = 0;
        resp.data.data.sections.forEach((s: { volume: number }) => (volume += s.volume));

        const area = resp.data.area ?? resp.data.data?.area;
        const newProperty: Property = {
          ...(resp.data as Property),
          volume,
          area: Math.round(area),
          ready: true
        };

        setOneProp(newProperty);
        handleClose();
        // FIXME: needed a full reload since tags and border didnt show up
        window.location.reload();
        return;
      } else {
        setErrorMessage({
          title: 'Skogsbruksplanen kunde inte skapas',
          subtitle: 'Det uppstod ett problem med att skapa skogsbruksplanen för',
          designation: uploadedFile.name,
          helperInfo: 'Kontakta oss på information@treebula.se eller 08-33 99 44 så hjälper vi dig.'
        });
      }
    } catch {
      setErrorMessage({
        title: 'Skogsbruksplanen kunde inte skapas',
        subtitle: 'Det uppstod ett problem med att skapa skogsbruksplanen för',
        designation: uploadedFile.name,
        helperInfo: 'Kontakta oss på information@treebula.se eller 08-33 99 44 så hjälper vi dig.',
        onPrimaryAction: () => {
          setErrorMessage(undefined);
          generateForestandSBP(linkedProperty);
        },
        primaryActionText: 'Försök igen'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DynamicModal
        isOpen={isOpen}
        onClose={onClose}
        className={cn('flex-col', className)}
        bp={{ sm: 'drawer', lg: 'sheet' }}
        drawerSize="full"
      >
        <DynamicModal.Header>
          <div>
            <Text variant="text-lg/leading-normal/medium">Lägg till fastighet</Text>
            <Text variant="text-sm/leading-normal/medium" className="text-[#737373]">
              Hur vill du lägga till?
            </Text>
          </div>
          <Button variant="tertiary" size="icon" onClick={handleClose}>
            <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
          </Button>
        </DynamicModal.Header>
        <DynamicModal.Content className="bg-[#F9F9F9]">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Text variant="text-sm/leading-normal/medium">1. Ladda upp forestand-fil</Text>
              <FileUpload
                accept=".xml"
                value={uploadedFile}
                onChange={setUploadedFile}
                validate={validateForestandFile}
                label="Dra filen hit"
                description="eller välj från din enhet"
                errorText={{
                  title: 'Ogiltig XML-fil',
                  description:
                    'Vi kunde inte läsa filen. Kontrollera att det är en skogsbruksplan i ForestandData XML-format.'
                }}
              />

              <HelperText
                label="Endast Forestand XML-format är tillåten."
                className="px-3 text-[#737373]"
              />
            </div>
            {/*
              Step 2 – fastighetskoppling (avkommentera för att återvisa)
            <Text variant="text-sm/leading-normal/medium">2. Koppla fastigheten</Text>
            {!linkedProperty ? (
              <Button
                variant="tertiary"
                className="relative flex w-full flex-row items-center justify-between rounded-lg bg-[#FFFFFF] text-left"
                disabled={!uploadedFile}
                onClick={() => setIsPropertySelectorOpen(true)}
              >
                <div className="flex flex-row items-center gap-3">
                  <Icon
                    name="MagnifyingGlassSimple"
                    className="fill-charcoal-500"
                    size="large"
                  />
                  <div className="flex flex-col gap-1">
                    <Text variant="text-sm/leading-normal/medium">Sök efter fastighet</Text>
                    <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                      Hitta ansluten fastighet hos Lantmäteriet
                    </Text>
                  </div>
                </div>
                <Icon name="ArrowShortSimple" size="small" className="rotate-180" />
              </Button>
            ) : (
              <div className="flex w-full flex-row items-center justify-between rounded-lg bg-amber-50 p-4">
                <div className="flex flex-row items-center gap-3">
                  <Icon name="CheckFilled" className="fill-[#349269]" size="large" />
                  <div className="flex flex-col gap-1">
                    <Text variant="text-sm/leading-normal/medium" className="capitalize">
                      {linkedProperty.beteckning}
                    </Text>
                    <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                      Kopplad fastighet från Lantmäteriet
                    </Text>
                  </div>
                </div>
                <Button variant="tertiary" size="icon" onClick={handleRemoveLinkedProperty}>
                  <Icon name="CrossSimple" className="fill-[#0F0F0F]" size="large" />
                </Button>
              </div>
            )}
            */}
          </div>
        </DynamicModal.Content>
        <DynamicModal.Footer className="flex gap-3">
          <Button
            fullWidth
            variant="primary"
            disabled={!uploadedFile || isLoading}
            onClick={() => generateForestandSBP()}
          >
            {!isLoading && <Icon name="PlusSimple" className="fill-[#FBFBFB]" />}
            {isLoading ? 'Importerar...' : 'Lägg till fastighet'}

            {isLoading && (
              <div className="border-white-50 h-4 w-4 animate-spin rounded-full border-2 border-solid border-t-transparent" />
            )}
          </Button>
          <Button fullWidth variant="tertiary" onClick={handleClose} disabled={isLoading}>
            Avbryt
          </Button>
        </DynamicModal.Footer>
      </DynamicModal>

      {/*
      <SearchAndAddProperties
        isOpen={isPropertySelectorOpen}
        onClose={() => setIsPropertySelectorOpen(false)}
        onSelect={handlePropertySelect}
      />
      */}

      {errorMessage && (
        <AlertDialog {...errorMessage} isOpen={!!errorMessage} onClose={handleCloseErrorModal} />
      )}
    </>
  );
};

export default UploadForestandFile;
